<script setup>
import { ref, computed, onMounted } from 'vue'
import { Head } from '@inertiajs/vue3'
import { trackEvent } from '@/composables/useAnalytics'
import AppLayout from '@/Layouts/AppLayout.vue'
import ProgressIndicator from '@/Components/Onboarding/ProgressIndicator.vue'
import StepPersonalInformation from '@/Components/Onboarding/StepPersonalInformation.vue'
import StepServicesOffered from '@/Components/Onboarding/StepServicesOffered.vue'
import StepPricing from '@/Components/Onboarding/StepPricing.vue'
import StepBankDetails from '@/Components/Onboarding/StepBankDetails.vue'
import ApplicationSubmitted from '@/Components/Onboarding/ApplicationSubmitted.vue'

const props = defineProps({
  subjects: Array,
  examboards: Array,
  levels: Array,
  experiences: Array,
  styles: Array,
  teachingMethods: Array,
  teachingExperiences: Array,
  pricingLimits: Object,
  maxGroupSessionsPerCourse: { type: Number, default: 4 },
  maxOneToOneSessions: { type: Number, default: 10 },
  additionalServices: Array,
  qualificationOptions: Object,
})

const currentStep = ref(1)
const tutorSlug = ref(null)
const applicationData = ref(null)
const loading = ref(false)

// Load application data from backend
const loadApplicationData = async (slug) => {
  if (!slug) return

  loading.value = true
  try {
    const response = await fetch('/become-a-tutor/load', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({ slug })
    })

    const data = await response.json()

    if (data.application) {
      applicationData.value = data.application
      currentStep.value = data.current_step || 1
    }

    if (data.slug) {
      tutorSlug.value = data.slug
      localStorage.setItem('tutor_onboarding_slug', data.slug)
    }
  } catch (err) {
    console.error('Failed to load application data:', err)
  } finally {
    loading.value = false
  }
}

// Load from localStorage on mount
onMounted(async () => {
  const savedSlug = localStorage.getItem('tutor_onboarding_slug')

  if (savedSlug) {
    tutorSlug.value = savedSlug
    await loadApplicationData(savedSlug)
  }
})

const stepTitles = {
  1: 'Personal Information',
  2: 'Services Offered',
  3: 'Pricing & Availability',
  4: 'Bank Details'
}
const stepTitlesMobile = {
  1: 'Personal',
  2: 'Subjects',
  3: 'Schedule',
  4: 'Verify'
}

// Computed: Get selected subjects from application data
const selectedSubjects = computed(() => {
  if (!applicationData.value?.subject_ids) return []

  return props.subjects.filter(subject =>
    applicationData.value.subject_ids.includes(subject.id)
  )
})

// Computed: Get selected teaching method IDs from application data
const selectedTeachingMethodIds = computed(() => {
  return applicationData.value?.teaching_method_ids || []
})

// Computed: Get selected levels from application data
const selectedLevels = computed(() => {
  if (!applicationData.value?.level_ids) return []

  return props.levels.filter(level =>
    applicationData.value.level_ids.includes(level.id)
  )
})

const progressPercentage = computed(() => {
  return (currentStep.value / 4) * 100
})

const pageTitle = computed(() => {
  return `Become a Tutor - ${stepTitles[currentStep.value]}`
})

const handleNext = (data) => {
  if (data?.slug) {
    tutorSlug.value = data.slug
    localStorage.setItem('tutor_onboarding_slug', data.slug)
  }

  if (data?.application) {
    applicationData.value = data.application
    // Update current step from application data
    currentStep.value = data.application.current_step || currentStep.value + 1
  } else {
    currentStep.value++
  }

  // Scroll to top of form section
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const handleBack = () => {
  if (currentStep.value > 1) {
    currentStep.value--
  }

  // Scroll to top of form section
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const handleComplete = (data) => {
  if (data?.application) {
    applicationData.value = data.application
    currentStep.value = data.application.current_step || 5
  } else {
    currentStep.value = 5
  }

  trackEvent('submitApplication', { application_type: 'tutor' })

  // Scroll to top when showing completion message
  window.scrollTo({ top: 0, behavior: 'smooth' })
}
</script>

<template>
  <Head :title="pageTitle" />

  <AppLayout>
    <!-- Application Submitted (Step 5) -->
    <ApplicationSubmitted
      v-if="currentStep === 5"
      :application="applicationData"
    />

    <template v-else>
      <!-- Hero Section -->
      <section class="bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 md:py-16">
        <div class="max-w-7xl mx-auto px-4 md:px-8">
          <div class="md:grid grid-cols-12 gap-12 items-center">
            <div class="col-span-5">
              <!-- <div class="mb-4">
                <span class="inline-block px-4 py-2 bg-blue-100 text-brand-blue rounded-full text-sm font-semibold">
                  <span class="material-symbols-outlined mr-2 text-base align-middle">star</span>
                  Join 10,000+ tutors
                </span>
              </div> -->
              <h1 class="text-center md:text-left text-2xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Share Your Knowledge,<br>
                <span class="text-brand-blue">Inspire Students</span>
              </h1>
              <p class="hidden md:block text-xl text-gray-600 mb-8 leading-relaxed">
                Become a tutor with Bindle and connect with students who need your expertise. Set your own hours, rates, and make a real difference in education.
              </p>
              <p class="md:hidden text-center font-bold text-gray-600 mb-8 leading-relaxed">
                  Become a tutor with Bindle and connect with students who need your expertise.
              </p>
              <div class="hidden md:flex flex-col md:items-center space-x-6 gap-4 md:gap-0 mb-8 md:mb-0">
                <div class="flex items-center space-x-2">
                  <span class="material-symbols-outlined text-brand-green text-xl">check_circle</span>
                  <span class="text-gray-700 font-medium">Flexible Schedule</span>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="material-symbols-outlined text-brand-green text-xl">check_circle</span>
                  <span class="text-gray-700 font-medium">Competitive Pay</span>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="material-symbols-outlined text-brand-green text-xl">check_circle</span>
                  <span class="text-gray-700 font-medium">Free Training</span>
                </div>
              </div>
            </div>

            <div class="hidden md:block col-span-7">
              <div class="bg-white rounded-2xl shadow-2xl p-8">
                <div class="flex items-center justify-between mb-8">
                  <div>
                    <h2 class="text-2xl font-bold text-gray-900">Start Your Application</h2>
                    <p class="text-gray-600 mt-1">Complete your tutor profile in 4 simple steps</p>
                  </div>
                  <button class="text-gray-400 hover:text-gray-600">
                    <span class="material-symbols-outlined text-2xl">help</span>
                  </button>
                </div>

                <!-- Step Indicators -->
                <div class="mb-10">
                  <div class="flex items-center justify-between mb-4">
                    <div
                      v-for="step in 4"
                      :key="step"
                      class="flex items-center space-x-3 transition-opacity"
                      :class="currentStep >= step ? '' : 'opacity-40'"
                    >
                      <div
                        class="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                        :class="currentStep >= step ? 'bg-brand-blue' : 'bg-gray-200'"
                      >
                        <span
                          class="font-bold"
                          :class="currentStep >= step ? 'text-white' : 'text-gray-600'"
                        >
                          {{ step }}
                        </span>
                      </div>
                      <div>
                        <p
                          class="text-sm font-semibold transition-colors"
                          :class="currentStep >= step ? 'text-gray-900' : 'text-gray-700'"
                        >
                          {{ stepTitles[step] }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      class="bg-gradient-to-r from-brand-blue to-brand-purple h-2.5 rounded-full transition-all duration-500"
                      :style="{ width: `${progressPercentage}%` }"
                    ></div>
                  </div>
                </div>

                <div class="grid grid-cols-3 gap-4 mb-8">
                  <div class="bg-blue-50 rounded-xl p-4 text-center">
                    <span class="material-symbols-outlined text-brand-blue text-2xl mb-2 block">schedule</span>
                    <p class="text-2xl font-bold text-gray-900">5 min</p>
                    <p class="text-xs text-gray-600">To complete</p>
                  </div>
                  <div class="bg-green-50 rounded-xl p-4 text-center">
                    <span class="material-symbols-outlined text-brand-green text-2xl mb-2 block">payments</span>
                    <p class="text-2xl font-bold text-gray-900">£25-50</p>
                    <p class="text-xs text-gray-600">Per hour avg</p>
                  </div>
                  <div class="bg-purple-50 rounded-xl p-4 text-center">
                    <span class="material-symbols-outlined text-brand-purple text-2xl mb-2 block">groups</span>
                    <p class="text-2xl font-bold text-gray-900">1000+</p>
                    <p class="text-xs text-gray-600">Active students</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="md:hidden">
              <div class="grid grid-cols-3 gap-4 mb-8">
                    <div class="bg-blue-50 rounded-xl p-4 text-center">
                        <span class="material-symbols-outlined text-brand-blue text-2xl mb-2 block">schedule</span>
                        <p class="text-xl font-bold text-gray-900">5 min</p>
                        <p class="font-bold text-xs text-gray-600">To complete</p>
                    </div>
                    <div class="bg-green-50 rounded-xl p-4 text-center">
                        <span class="material-symbols-outlined text-brand-green text-2xl mb-2 block">payments</span>
                        <p class="text-xl font-bold text-gray-900">£25-50</p>
                        <p class="font-bold text-xs text-gray-600">Per hour</p>
                    </div>
                    <div class="bg-purple-50 rounded-xl p-4 text-center">
                        <span class="material-symbols-outlined text-brand-purple text-2xl mb-2 block">groups</span>
                        <p class="text-xl font-bold text-gray-900">1000+</p>
                        <p class="font-bold text-xs text-gray-600">Students</p>
                    </div>
                </div>
              <div class="bg-white rounded-2xl shadow-2xl p-4">
                  <div class="flex items-center justify-between mb-3">
                      <div>
                          <h2 class="text-lg font-bold text-gray-900">Application Progress</h2>
                      </div>
                      <span class="font-bold text-sm text-gray-400 ">
                          Step {{currentStep}} of 4
                      </span>
                  </div>

                  <div class="w-full bg-gray-200 rounded-full h-2.5 mb-6">
                      <div
                          class="bg-gradient-to-r from-brand-blue to-brand-purple h-2.5 rounded-full transition-all duration-500"
                          :style="{ width: `${progressPercentage}%` }"
                      ></div>
                  </div>

                  <!-- Step Indicators -->
                  <div class="mb-2">
                      <div class="grid grid-cols-4 items-center justify-between mb-4">
                          <div
                              v-for="step in 4"
                              :key="step"
                              class="flex flex-col justify-center items-center gap-2 transition-opacity"
                              :class="currentStep >= step ? '' : 'opacity-40'"
                          >
                              <div
                                  class="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                                  :class="currentStep >= step ? 'bg-brand-blue' : 'bg-gray-200'"
                              >
                                <span
                                    class="font-bold text-sm"
                                    :class="currentStep >= step ? 'text-white' : 'text-gray-600'"
                                >
                                  {{ step }}
                                </span>
                              </div>
                              <div>
                                  <p
                                      class="text-sm font-bold tracking-tight transition-colors"
                                      :class="currentStep >= step ? 'text-gray-900' : 'text-gray-700'"
                                  >
                                      {{ stepTitlesMobile[step] }}
                                  </p>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
            </div>


          </div>
        </div>
      </section>

      <!-- Main Form Section -->
      <section class="py-4 md:py-16">
        <div class="max-w-7xl mx-auto px-4 md:px-8">
          <!-- Progress Indicator -->
          <ProgressIndicator :current-step="currentStep" />

          <!-- Form Content -->
          <!-- Step 1: Personal Information -->
          <StepPersonalInformation
            v-if="currentStep === 1"
            :subjects="subjects"
            :levels="levels"
            :teaching-experiences="teachingExperiences"
            :qualification-options="qualificationOptions"
            :application="applicationData"
            :slug="tutorSlug"
            @next="handleNext"
          />

          <!-- Step 2: Services Offered -->
          <StepServicesOffered
            v-else-if="currentStep === 2"
            :teaching-methods="teachingMethods"
            :additional-services="additionalServices"
            :application="applicationData"
            :slug="tutorSlug"
            @next="handleNext"
            @back="handleBack"
          />

          <!-- Step 3: Pricing & Availability -->
          <StepPricing
            v-else-if="currentStep === 3"
            :pricing-limits="pricingLimits"
            :max-group-sessions-per-course="maxGroupSessionsPerCourse"
            :max-one-to-one-sessions="maxOneToOneSessions"
            :user-subjects="selectedSubjects"
            :user-levels="selectedLevels"
            :teaching-method-ids="selectedTeachingMethodIds"
            :teaching-methods="teachingMethods"
            :application="applicationData"
            :slug="tutorSlug"
            @next="handleNext"
            @back="handleBack"
          />

          <!-- Step 4: Bank Details -->
          <StepBankDetails
            v-else-if="currentStep === 4"
            :application="applicationData"
            :slug="tutorSlug"
            @complete="handleComplete"
            @back="handleBack"
          />
        </div>
      </section>
    </template>
  </AppLayout>
</template>
